import { InTopic } from './InTopic';
import { AnyInMessageTopic } from './InMessageTopic';
import { bootstrapInTopics } from './bootstrapper';
import { setGlobalWindowVariable } from 'utils/globalWindowVariables';
import { Store } from 'redux';
import State from 'store/State';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import {
    embeddedApiReceiveMessageAction,
    embeddedApiUpdateReceivedMessageStatus,
} from 'embedded/api/store/actions';
import { v4 as uuid } from 'uuid';
import { InMessageError } from './InMessageError';
import { selectLoginState } from 'login/store/selectors';
import { LoginStatus } from 'login/store/LoginState';
import orderBy from 'lodash/orderBy';
import { EmbeddedApiServiceOptions } from 'embedded/api/EmbeddedApiService';

export class InMessagesProcessor {
    private readonly inTopics: Record<InTopic, AnyInMessageTopic>;

    private readonly logger: Logger;
    private readonly loggingService: LoggingService;

    public store: Store<State> | undefined;

    public constructor(options: EmbeddedApiServiceOptions) {
        const { loggingService } = options;
        this.logger = buildLogger('InMessagesProcessor', loggingService);
        this.loggingService = loggingService;
        this.inTopics = bootstrapInTopics();

        setGlobalWindowVariable({
            send: (topic, payload) =>
                this.receiveMessage(topic as InTopic, payload),
        });
    }

    public receiveMessage<TPayload>(
        topic: InTopic,
        payload: TPayload
    ): Promise<string> {
        if (!this.store) {
            throw new Error('Store has not been set');
        }
        const uid = uuid();

        const inTopic = this.inTopics[topic];

        const inMessageAcceptanceError = this.assertOkToAcceptIncoming(
            topic,
            payload,
            uid,
            inTopic
        );

        if (inMessageAcceptanceError) {
            this.logger.warn(`Unable to process message ${topic}`, {
                payload,
                error: inMessageAcceptanceError,
            });

            this.store!.dispatch(
                embeddedApiReceiveMessageAction(
                    uid,
                    topic,
                    payload,
                    'Error',
                    inMessageAcceptanceError.type,
                    inMessageAcceptanceError.details
                )
            );
            return Promise.reject(inMessageAcceptanceError);
        }

        if (!this.checkAuthenticationStatus(payload, uid, inTopic)) {
            return Promise.resolve(
                `${topic} will be processed on authentication`
            );
        }

        setTimeout(() => {
            this.handleIncomingMessage(topic, payload, uid, inTopic);
        }, 0);

        this.logger.debug(`Accepted message ${topic} for processing`, {
            payload,
        });

        this.store!.dispatch(
            embeddedApiReceiveMessageAction(uid, topic, payload, 'Received')
        );

        return Promise.resolve(`${topic} accepted for processing`);
    }

    private assertOkToAcceptIncoming<TPayload>(
        topic: InTopic,
        payload: TPayload,
        uid: string,
        inTopic: AnyInMessageTopic
    ): InMessageError | undefined {
        if (!inTopic) {
            this.logger.warn(`Invalid topic '${topic}' received`, {
                payload,
            });
            return {
                type: 'Invalid Topic',
                message: `Invalid topic '${topic}' received`,
            };
        }

        return inTopic.assertOkToAcceptIncoming(uid, payload, this.store!);
    }

    private checkAuthenticationStatus<TPayload>(
        payload: TPayload,
        uid: string,
        inMessage: AnyInMessageTopic
    ): boolean {
        if (inMessage.requirements.mustBeAuthenticated) {
            const loginState = selectLoginState(this.store!.getState());
            if (loginState.status !== LoginStatus.SUCCESS) {
                this.logger.debug(
                    'Saving message for processing once authenticated',
                    payload
                );
                inMessage.savePendingAuthentication(payload, uid);
                return false;
            }
        }
        return true;
    }

    private handleIncomingMessage<TPayload>(
        topic: InTopic,
        payload: TPayload,
        uid: string,
        inTopic: AnyInMessageTopic
    ) {
        this.logger.debug(`Processing message ${topic}`, { payload });
        this.store!.dispatch(
            embeddedApiUpdateReceivedMessageStatus(uid, 'Processing')
        );

        try {
            inTopic.handle(uid, payload, this.store!, this.loggingService);
            this.store!.dispatch(
                embeddedApiUpdateReceivedMessageStatus(uid, 'Success')
            );
        } catch (err) {
            this.logger.critical(`Error processing message ${topic}`, {
                error: err,
                payload,
            });
            this.store!.dispatch(
                embeddedApiUpdateReceivedMessageStatus(
                    uid,
                    'Error',
                    'Processing Failed',
                    err as Error
                )
            );
        }
    }

    public processPendingAuthenticationMessages() {
        const pendingMessages = Object.keys(this.inTopics)
            .map((topic) => ({
                topic: topic as InTopic,
                pending: this.inTopics[topic as InTopic].pendingAuthentication,
            }))
            .filter((v) => !!v.pending)
            .map((v) => ({ topic: v.topic, ...v.pending }));

        const pendingMessagesByTimeReceived = orderBy(
            pendingMessages,
            'timeStamp'
        );

        for (const message of pendingMessagesByTimeReceived) {
            this.receiveMessage(message.topic, message.payload);
            this.inTopics[message.topic].pendingAuthentication = undefined;
        }
    }
}
